/**
 * The glyph atlas behind every enemy mark: role badges, status icons and the digits of the floating numbers, drawn
 * once into one 512×256 canvas (one texture, one upload, one sampler). The in-world badges (enemyMarks.ts) and the
 * HUD's wave preview draw the same glyphs, so the icon on the preview is the icon over the unit.
 *
 * Glyphs are white with a dark outline baked in: the shaders tint the white (status colours) and keep the outline,
 * so a glyph reads on any background. Shapes are distinct in silhouette alone, never only in colour.
 */
import * as THREE from "three";
import type { EnemyRole, EnemyStatus } from "./enemyLook";

export const ATLAS_COLS = 8;
/** Eight rows (512 × 512): rows 0–3 the original set, row 4 the M21 enemies and their statuses (ENEMIES_V2). */
export const ATLAS_ROWS = 8;
const CELL = 64;
/** Glyphs are drawn inside the cell with this margin so mip levels do not bleed a neighbour in. */
const INSET = 0.86;
const OUTLINE = "#12141c";

export const ROLE_CELL: Readonly<Record<EnemyRole, number>> = {
  raider: 0, runner: 1, brute: 2, shield: 3, healer: 4, flyer: 5, bomber: 6, boss: 7,
  summoner: 32, imp: 33, burrower: 34, spellward: 35, stunner: 36,
};
export const STATUS_CELL: Readonly<Record<EnemyStatus, number>> = {
  slowed: 8, burning: 9, armour: 10, shred: 11, boosted: 12, shielded: 15,
  // elite affixes (DESIGN §5.1), after the digits
  warded: 26, fireproof: 27, frostbound: 28, splitting: 29,
  // ENEMIES_V2
  burrowed: 37, casting: 38,
  // M36 immune units: a shield with what it shrugs off cut into it
  immPhysical: 40, immMagic: 41, immFire: 42, immSlow: 43,
};
/** M36: the rare spawn's star (the wave chip and the enemy card). */
export const RARE_CELL = 44;
export const PLUS_CELL = 13;
export const MINUS_CELL = 14;
/** Digits 0–9 occupy cells 16–25. */
export const DIGIT_CELL = 16;
/** M36: the letters the IMMUNE popup spells, drawn like the digits (cells 45–49). */
export const LETTER_CELL: Readonly<Record<string, number>> = { I: 45, M: 46, U: 47, N: 48, E: 49 };

type Draw = (c: CanvasRenderingContext2D) => void;
/** A filled shape in a 64-unit box: outline first, then white. */
const fill = (path: string): Draw => (c) => { const p = new Path2D(path); c.lineWidth = 9; c.strokeStyle = OUTLINE; c.stroke(p); c.fillStyle = "#fff"; c.fill(p); };
/** A stroked shape (lines): a wide dark stroke under a narrower white one. */
const lines = (path: string, w = 7): Draw => (c) => { const p = new Path2D(path); c.lineCap = "round"; c.lineWidth = w + 7; c.strokeStyle = OUTLINE; c.stroke(p); c.lineWidth = w; c.strokeStyle = "#fff"; c.stroke(p); };

const GLYPHS: Readonly<Record<number, Draw>> = {
  // raider: a sword
  0: fill("M32 4 L39 13 L39 38 L50 38 L50 45 L36 45 L36 54 A5 5 0 1 1 28 54 L28 45 L14 45 L14 38 L25 38 L25 13 Z"),
  // runner: double chevron, pointing ahead
  1: fill("M6 14 L18 14 L32 32 L18 50 L6 50 L20 32 Z M28 14 L40 14 L56 32 L40 50 L28 50 L42 32 Z"),
  // brute: a war hammer
  2: fill("M10 8 L54 8 L54 30 L38 30 L38 58 L26 58 L26 30 L10 30 Z"),
  // shieldbearer: a heater shield with a boss stripe
  3: fill("M12 8 L52 8 L52 30 Q52 50 32 60 Q12 50 12 30 Z"),
  // healer: a cross
  4: fill("M25 8 L39 8 L39 25 L56 25 L56 39 L39 39 L39 56 L25 56 L25 39 L8 39 L8 25 L25 25 Z"),
  // flyer: spread wings
  5: fill("M32 46 Q22 26 4 16 Q10 30 8 40 Q18 36 26 44 L32 52 L38 44 Q46 36 56 40 Q54 30 60 16 Q42 26 32 46 Z"),
  // bomber: a round bomb with a fuse and spark
  6: (c) => { fill("M27 20 A20 20 0 1 0 28 20 Z M34 22 L44 11 L49 16 L39 26 Z")(c); fill("M50 3 L53 9 L59 10 L54 14 L55 20 L50 17 L45 20 L46 14 L41 10 L47 9 Z")(c); },
  // boss: a crown
  7: fill("M6 50 L8 16 L21 30 L32 8 L43 30 L56 16 L58 50 Z"),
  // slowed: a snowflake
  8: lines("M32 6 L32 58 M9 19 L55 45 M9 45 L55 19 M24 10 L32 18 L40 10 M24 54 L32 46 L40 54", 6),
  // burning: a flame
  9: fill("M32 4 Q50 22 48 40 Q46 58 32 60 Q18 58 16 42 Q16 30 26 20 Q26 34 33 36 Q38 22 32 4 Z"),
  // armour: a hexagonal plate
  10: fill("M32 4 L56 18 L56 46 L32 60 L8 46 L8 18 Z"),
  // shred: a crack
  11: lines("M20 4 L38 22 L24 34 L44 60", 8),
  // boosted: a lightning bolt
  12: fill("M38 2 L12 36 L29 36 L22 62 L52 24 L35 24 L44 2 Z"),
  13: fill("M26 12 L38 12 L38 26 L52 26 L52 38 L38 38 L38 52 L26 52 L26 38 L12 38 L12 26 L26 26 Z"),
  14: fill("M12 26 L52 26 L52 38 L12 38 Z"),
  // shielded (behind a shieldbearer's wall): the shieldbearer's own shield, as a status
  15: fill("M12 8 L52 8 L52 30 Q52 50 32 60 Q12 50 12 30 Z"),
  // warded: a ward ring (physical hits glance off)
  26: lines("M32 8 A24 24 0 1 1 31.9 8 Z", 9),
  // fireproof: a flame struck through
  27: (c) => { fill("M32 6 Q48 22 46 40 Q44 56 32 58 Q20 56 18 42 Q18 30 27 21 Q27 34 33 36 Q37 24 32 6 Z")(c); lines("M10 54 L54 10", 7)(c); },
  // frostbound: an ice crystal (slows half as hard)
  28: fill("M32 4 L52 32 L32 60 L12 32 Z"),
  // splitting: two beads (it breaks in two)
  29: fill("M6 32 A12 12 0 1 0 30 32 A12 12 0 1 0 6 32 Z M34 32 A12 12 0 1 0 58 32 A12 12 0 1 0 34 32 Z"),
  // ---- ENEMIES_V2 (docs/ENEMIES_V2.md)
  // summoner: a five-pointed summoning star
  32: fill("M32 4 L39 24 L60 24 L43 37 L49 58 L32 45 L15 58 L21 37 L4 24 L25 24 Z"),
  // imp: a horned little head
  33: fill("M10 6 L22 22 Q32 18 42 22 L54 6 L52 28 Q56 36 52 46 Q44 58 32 58 Q20 58 12 46 Q8 36 12 28 Z"),
  // burrower: a digging claw, three talons down into a mound
  34: (c) => { fill("M4 58 Q32 34 60 58 Z")(c); lines("M18 6 Q16 26 24 40 M32 4 L32 40 M46 6 Q48 26 40 40", 7)(c); },
  // spellward: a rune shield (a hexagon with a ward rune)
  35: (c) => { fill("M32 3 L57 17 L57 47 L32 61 L7 47 L7 17 Z")(c); lines("M32 48 L32 18 M32 30 L22 20 M32 30 L42 20", 5)(c); },
  // stunner: a fist striking the ground, the shock rising off it
  36: (c) => { fill("M20 26 L44 26 L44 46 L20 46 Z M8 54 L56 54 L56 60 L8 60 Z")(c); lines("M6 40 Q2 30 8 20 M58 40 Q62 30 56 20", 5)(c); },
  // burrowed (status): a crosshair struck through — no tower can find it
  37: (c) => { lines("M32 10 A22 22 0 1 1 31.9 10 Z M32 2 L32 18 M32 46 L32 62 M2 32 L18 32 M46 32 L62 32", 5)(c); lines("M10 54 L54 10", 7)(c); },
  // casting (status): an hourglass — it stands still, strike now
  38: fill("M14 4 L50 4 L50 12 L36 32 L50 52 L50 60 L14 60 L14 52 L28 32 L14 12 Z"),
  // ---- M36 immune units: one shield silhouette (immunity reads as "warded against"), the damage it ignores cut dark into it
  40: immune("M32 16 L36 21 L36 36 L42 36 L42 40 L35 40 L35 46 L29 46 L29 40 L22 40 L22 36 L28 36 L28 21 Z"),        // a sword: physical
  41: immune("M32 14 L35.5 25 L47 25 L38 32 L41.5 43 L32 36.5 L22.5 43 L26 32 L17 25 L28.5 25 Z"),                   // a star: magic
  42: immune("M32 13 Q43 24 42 34 Q41 45 32 46 Q23 45 22 36 Q22 29 28 23 Q28 31 32.5 32 Q35 24 32 13 Z"),          // a flame: fire
  43: (c) => { immune("")(c); const p = new Path2D("M32 15 L32 45 M19 22.5 L45 37.5 M19 37.5 L45 22.5"); c.lineCap = "round"; c.lineWidth = 4.5; c.strokeStyle = OUTLINE; c.stroke(p); },   // a snowflake: slows
  // the rare's star: a four-point sparkle with a small companion
  44: fill("M30 2 L35 25 L58 30 L35 35 L30 58 L25 35 L2 30 L25 25 Z M50 4 L52 11 L59 13 L52 15 L50 22 L48 15 L41 13 L48 11 Z"),
};

/** A shield (white, outlined) with `cut` filled dark inside it: the immune glyphs (M36). */
function immune(cut: string): Draw {
  return (c) => {
    fill("M8 6 L56 6 L56 30 Q56 52 32 62 Q8 52 8 30 Z")(c);
    if (cut) { c.fillStyle = OUTLINE; c.fill(new Path2D(cut)); }
  };
}

let canvas: HTMLCanvasElement | null = null;
let texture: THREE.CanvasTexture | null = null;

function drawAtlas(): HTMLCanvasElement {
  if (canvas) return canvas;
  canvas = document.createElement("canvas");
  canvas.width = CELL * ATLAS_COLS;
  canvas.height = CELL * ATLAS_ROWS;
  const c = canvas.getContext("2d")!;
  c.lineJoin = "round";
  const place = (cell: number, draw: Draw) => {
    c.save();
    c.translate((cell % ATLAS_COLS) * CELL + CELL * (1 - INSET) / 2, Math.floor(cell / ATLAS_COLS) * CELL + CELL * (1 - INSET) / 2);
    c.scale(INSET * CELL / 64, INSET * CELL / 64);
    draw(c);
    c.restore();
  };
  for (const [cell, draw] of Object.entries(GLYPHS)) place(Number(cell), draw);
  for (const [ch, cell] of Object.entries(LETTER_CELL)) {
    place(cell, (x) => {
      x.font = "900 54px 'Fredoka Display', system-ui, -apple-system, 'Segoe UI', sans-serif";
      x.textAlign = "center"; x.textBaseline = "middle";
      x.lineWidth = 10; x.strokeStyle = OUTLINE; x.strokeText(ch, 32, 35);
      x.fillStyle = "#fff"; x.fillText(ch, 32, 35);
    });
  }
  for (let d = 0; d <= 9; d++) {
    place(DIGIT_CELL + d, (x) => {
      x.font = "900 60px 'Fredoka Display', system-ui, -apple-system, 'Segoe UI', sans-serif";
      x.textAlign = "center"; x.textBaseline = "middle";
      x.lineWidth = 10; x.strokeStyle = OUTLINE; x.strokeText(String(d), 32, 35);
      x.fillStyle = "#fff"; x.fillText(String(d), 32, 35);
    });
  }
  return canvas;
}

/** The shared atlas texture (built on first use). */
export function glyphTexture(): THREE.CanvasTexture {
  if (texture) return texture;
  texture = new THREE.CanvasTexture(drawAtlas());
  texture.generateMipmaps = true;
  texture.minFilter = THREE.LinearMipmapLinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.anisotropy = 1;
  return texture;
}

const badgeCache = new Map<string, string>();

/**
 * A badge as an image URL for the DOM (the HUD's wave preview): the disc in `colour`, the role's glyph in white, a dark
 * rim (gold for bosses) — the same picture the shader draws over the unit. Cached per role and colour.
 */
export function badgeDataUrl(role: EnemyRole, colour: number, px = 44): string {
  const key = `${role}|${colour}|${px}`;
  const hit = badgeCache.get(key);
  if (hit) return hit;
  const atlas = drawAtlas();
  const cv = document.createElement("canvas");
  cv.width = cv.height = px;
  const c = cv.getContext("2d")!;
  const r = px / 2;
  c.fillStyle = role === "boss" ? "#ffd24a" : OUTLINE;
  c.beginPath(); c.arc(r, r, r, 0, Math.PI * 2); c.fill();
  c.fillStyle = `#${colour.toString(16).padStart(6, "0")}`;
  c.beginPath(); c.arc(r, r, r - Math.max(2, px * (role === "boss" ? 0.1 : 0.06)), 0, Math.PI * 2); c.fill();
  const cell = ROLE_CELL[role], g = r * 1.25;
  c.drawImage(atlas, (cell % ATLAS_COLS) * CELL, Math.floor(cell / ATLAS_COLS) * CELL, CELL, CELL, r - g / 2 * 1.0, r - g / 2 * 1.0, g, g);
  const url = cv.toDataURL("image/png");
  badgeCache.set(key, url);
  return url;
}

const glyphCache = new Map<string, string>();

/**
 * One atlas glyph on a dark disc as an image URL for the DOM (M36: the immunity marks on the wave chips and the enemy
 * card, the rare's star): the glyph tinted `colour`, the same picture the plate draws beside the badge.
 */
export function glyphDataUrl(cell: number, colour: number, px = 22): string {
  const key = `${cell}|${colour}|${px}`;
  const hit = glyphCache.get(key);
  if (hit) return hit;
  const atlas = drawAtlas();
  const cv = document.createElement("canvas");
  cv.width = cv.height = px;
  const c = cv.getContext("2d")!;
  const r = px / 2;
  c.fillStyle = OUTLINE;
  c.beginPath(); c.arc(r, r, r, 0, Math.PI * 2); c.fill();
  // tint: draw the glyph, then multiply its white by the colour (the dark outline stays dark)
  const g = r * 1.6;
  const tmp = document.createElement("canvas");
  tmp.width = tmp.height = Math.ceil(g);
  const t = tmp.getContext("2d")!;
  t.drawImage(atlas, (cell % ATLAS_COLS) * CELL, Math.floor(cell / ATLAS_COLS) * CELL, CELL, CELL, 0, 0, g, g);
  t.globalCompositeOperation = "multiply";
  t.fillStyle = `#${colour.toString(16).padStart(6, "0")}`;
  t.fillRect(0, 0, g, g);
  t.globalCompositeOperation = "destination-in";
  t.drawImage(atlas, (cell % ATLAS_COLS) * CELL, Math.floor(cell / ATLAS_COLS) * CELL, CELL, CELL, 0, 0, g, g);
  c.drawImage(tmp, r - g / 2, r - g / 2);
  const url = cv.toDataURL("image/png");
  glyphCache.set(key, url);
  return url;
}
